import { useEffect, useRef, useState, useCallback } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { useAppDispatch } from "../../hooks/sharedHooks";
import {
  createTrackingConnection,
  getTrackingConnection,
  stopTrackingConnection,
} from "../signalrTrackingConnection";
import {
  updatePosition,
  updateTrackingStatus,
  TrackingPosition,
} from "../trackingSlice";

interface PositionUpdateEvent {
  sessionId: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
  etaMinutes?: number;
  recordedAt: string;
}

interface TrackingStatusChangedEvent {
  sessionId: string;
  newStatus: number;
}

interface UseTrackingHubResult {
  latestPosition: TrackingPosition | null;
  trackingStatus: number | null;
  isConnected: boolean;
}

/**
 * Hook to subscribe to a specific tracking session via SignalR.
 * Connects to the TrackingHub, joins the tracking_{sessionId} group,
 * and dispatches PositionUpdate and TrackingStatusChanged events to Redux.
 *
 * @param sessionId - The tracking session ID to subscribe to (null to disconnect)
 */
const useTrackingHub = (sessionId: string | null): UseTrackingHubResult => {
  const dispatch = useAppDispatch();
  const connectionRef = useRef<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [latestPosition, setLatestPosition] =
    useState<TrackingPosition | null>(null);
  const [trackingStatus, setTrackingStatus] = useState<number | null>(null);

  const joinSession = useCallback(
    async (connection: HubConnection, sid: string) => {
      try {
        await connection.invoke("JoinTrackingSession", sid);
      } catch (err) {
        console.error("Failed to join tracking session:", err);
      }
    },
    []
  );

  const leaveSession = useCallback(
    async (connection: HubConnection, sid: string) => {
      try {
        if (connection.state === HubConnectionState.Connected) {
          await connection.invoke("LeaveTrackingSession", sid);
        }
      } catch (err) {
        console.error("Failed to leave tracking session:", err);
      }
    },
    []
  );

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let connection = getTrackingConnection();

    // Create connection if it doesn't exist
    if (!connection) {
      connection = createTrackingConnection();
    }

    if (!connection) {
      return;
    }

    connectionRef.current = connection;

    const onPositionUpdate = (event: PositionUpdateEvent) => {
      if (event.sessionId !== sessionId) return;

      const position: TrackingPosition = {
        sessionId: event.sessionId,
        latitude: event.latitude,
        longitude: event.longitude,
        accuracy: event.accuracy ?? null,
        speed: event.speed ?? null,
        heading: event.heading ?? null,
        etaMinutes: event.etaMinutes ?? null,
        recordedAt: event.recordedAt,
      };

      setLatestPosition(position);
      dispatch(updatePosition(position));
    };

    const onStatusChanged = (event: TrackingStatusChangedEvent) => {
      if (event.sessionId !== sessionId) return;

      setTrackingStatus(event.newStatus);
      dispatch(
        updateTrackingStatus({
          sessionId: event.sessionId,
          status: event.newStatus,
        })
      );
    };

    connection.on("PositionUpdate", onPositionUpdate);
    connection.on("TrackingStatusChanged", onStatusChanged);

    // On reconnect, re-join the session group
    const onReconnected = () => {
      setIsConnected(true);
      if (connectionRef.current && sessionId) {
        joinSession(connectionRef.current, sessionId);
      }
    };

    connection.onreconnected(onReconnected);

    connection.onreconnecting(() => {
      setIsConnected(false);
    });

    connection.onclose(() => {
      setIsConnected(false);
    });

    // Start connection and join group
    if (connection.state === HubConnectionState.Disconnected) {
      connection
        .start()
        .then(() => {
          setIsConnected(true);
          if (connectionRef.current && sessionId) {
            joinSession(connectionRef.current, sessionId);
          }
        })
        .catch((err) =>
          console.error("TrackingHub connection error:", err)
        );
    } else if (connection.state === HubConnectionState.Connected) {
      setIsConnected(true);
      joinSession(connection, sessionId);
    }

    return () => {
      if (connectionRef.current) {
        connectionRef.current.off("PositionUpdate", onPositionUpdate);
        connectionRef.current.off("TrackingStatusChanged", onStatusChanged);

        if (sessionId) {
          leaveSession(connectionRef.current, sessionId);
        }
      }
    };
  }, [sessionId, dispatch, joinSession, leaveSession]);

  // Cleanup connection on unmount
  useEffect(() => {
    return () => {
      stopTrackingConnection();
    };
  }, []);

  return { latestPosition, trackingStatus, isConnected };
};

export default useTrackingHub;
